import * as Sentry from '@sentry/react';
import { Frame } from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '@constants';
import {
	resolveOptimisticallyUpdatedFrames,
	setSceneLoadError,
	updateFrames,
} from '../redux/db/actions';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

export class FramePollingStream extends PollingStream<Frame> {
	constructor(activeSceneId: string) {
		const url = VITE_FORGE_URL + `/poll/scene/${activeSceneId}/frames`;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(setSceneLoadError('You do not have access to this project'));
				store.dispatch(updateFrames([]));
			} else {
				console.error(error);
			}
		};

		super(url, onError, 'activeScene.frames');
	}

	extractData(response: any): Frame[] {
		return response?.frames ?? [];
	}

	onData(frames: Frame[]) {
		// Resolve optimistic writes based on writeId from the database
		const writeIds = frames
			.map((frame) => frame.writeId)
			.filter((writeId): writeId is string => !!writeId);

		if (writeIds.length > 0) {
			store.dispatch(resolveOptimisticallyUpdatedFrames(writeIds));
		}

		// The backend already returns Frame objects with proper camelCase field names
		// (thanks to postgres.camel transform)
		// We just need to convert date strings to Date objects
		const processedFrames: Frame[] = frames.map((frame) => ({
			...frame,
			createdAt: new Date(frame.createdAt),
			updatedAt: frame.updatedAt ? new Date(frame.updatedAt) : undefined,
			deletedAt: frame.deletedAt ? new Date(frame.deletedAt) : null,
			// Check if this frame's scene is in the user's scenes collection
			// If scenes aren't loaded yet, this will default to viewOnly (fail gracefully)
			// When scenes do load, refreshFramePermissions will update these flags
			viewOnly: !store.getState().db.scenes.some((scene) => scene.id === frame.sceneId),
		}));

		store.dispatch(updateFrames(processedFrames));

		// Detect if optimistic updates have been applied
		// Get all write IDs that were resolved by the server
		const resolvedWriteIds = new Set(writeIds);

		// Check for any optimistic writes that weren't resolved
		const state = store.getState();
		const returnedFrameIds = new Set(frames.map((frame) => frame.id));
		const unresolvedWrites = state.db.optimisticFrameWrites
			.filter((write) => returnedFrameIds.has(write.value.id))
			.filter((write) => !resolvedWriteIds.has(write.id));

		if (unresolvedWrites.length > 0) {
			const now = Date.now();
			unresolvedWrites.forEach((write) => {
				if (write.value.createdAt instanceof Date) {
					const age = now - write.value.createdAt.getTime();
					if (age > 2500) {
						const message =
							`[PollingStream] Optimistic ${write.operation} for frame ${write.value.id} ` +
							`not resolved after ${age}ms (writeId: ${write.id})`;
						console.warn(message);
						Sentry.captureMessage(message, 'warning');
					}
				}
			});
		}
	}
}
